import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Button, Input, Textarea, Select, StatusBadge, EmptyState, CopyButton, ConfirmDialog } from '../../components/core';
import { http } from '../../lib/api';
import { useUiStore } from '../../store/ui';
import type { StreamDto, DestinationDto, StreamPlatform } from '@livestreamx/shared-types';
import { NAV } from './nav';

export function DestinationsPage() {
  const { id: streamId } = useParams<{ id: string }>();
  const [destinations, setDestinations] = useState<DestinationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ui = useUiStore();

  useEffect(() => {
    if (!streamId) return;
    let stopped = false;
    const tick = () =>
      http
        .get<DestinationDto[]>(`/api/tenant/streams/${streamId}/destinations`)
        .then((d) => {
          if (stopped) return;
          setDestinations(d);
          setError(null);
          setLoading(false);
        })
        .catch((err) => {
          if (stopped) return;
          setError(err instanceof Error ? err.message : 'Failed to load destinations');
          setLoading(false);
        });
    void tick();
    // Relay status is reported asynchronously by the controller (CONNECTING →
    // CONNECTED / RECONNECTING / FAILED), so a one-shot load freezes the badge
    // on whatever it was at mount. Poll instead.
    const timer = setInterval(tick, 3_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [streamId]);

  const toggleEnabled = async (d: DestinationDto) => {
    try {
      await http.post(`/api/tenant/streams/${streamId}/destinations/${d.id}/${d.enabled ? 'disable' : 'enable'}`);
      setDestinations((prev) => prev.map((p) => (p.id === d.id ? { ...p, enabled: !d.enabled, status: d.enabled ? 'DISABLED' : 'CONNECTING' } : p)));
      ui.toast('success', d.enabled ? `“${d.name}” disabled` : `“${d.name}” enabled`);
    } catch (err) {
      ui.toast('danger', 'Update failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  /**
   * Forces a fresh relay attempt. The controller reconciles on its poll, so a
   * re-push is just clearing the API-side FAILED/stale state back to
   * CONNECTING — the next controller poll spawns a new FFmpeg within ~3s.
   */
  const repush = async (d: DestinationDto) => {
    try {
      await http.post(`/api/tenant/streams/${streamId}/destinations/${d.id}/enable`, {});
      setDestinations((prev) =>
        prev.map((p) => (p.id === d.id ? { ...p, enabled: true, status: 'CONNECTING', lastError: null } : p)),
      );
      ui.toast('success', `Re-pushing to “${d.name}”…`);
    } catch (err) {
      ui.toast('danger', 'Re-push failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const testDest = async (d: DestinationDto) => {
    try {
      const r = await http.post<{ ok: boolean; latencyMs?: number; error?: string }>(
        `/api/tenant/streams/${streamId}/destinations/${d.id}/test`,
        {},
      );
      ui.toast(r.ok ? 'success' : 'danger', r.ok ? `Reachable in ${r.latencyMs}ms` : 'Unreachable', r.error);
    } catch (err) {
      ui.toast('danger', 'Test failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const deleteDest = async (d: DestinationDto) => {
    try {
      await http.delete(`/api/tenant/streams/${streamId}/destinations/${d.id}`);
      setDestinations((prev) => prev.filter((p) => p.id !== d.id));
      ui.toast('success', `“${d.name}” deleted`);
    } catch (err) {
      ui.toast('danger', 'Delete failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  if (loading) return <AppLayout brand="LiveStreamX" navGroups={NAV}><EmptyState title="Loading destinations…" /></AppLayout>;
  if (error && !destinations.length) return <AppLayout brand="LiveStreamX" navGroups={NAV}><EmptyState title="Error" body={error} /></AppLayout>;

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="flex justify-between items-center mb-2">
        <h1 className="page-title">Destinations</h1>
        <Link to={`/streams/${streamId}/destinations/create`}><Button>+ Add destination</Button></Link>
      </div>

      {destinations.length === 0 ? (
        <EmptyState
          title="No destinations"
          body="Add RTMP destinations to broadcast this stream to YouTube, Facebook, Twitch, or any custom RTMP endpoint."
          action={<Link to={`/streams/${streamId}/destinations/create`}><Button>Add destination</Button></Link>}
        />
      ) : (
        <div className="destinations-list">
          {destinations.map((d) => (
            <div key={d.id} className="destination-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <StatusBadge status={d.status} />
                  <div>
                    <div className="font-medium">{d.name}</div>
                    <div className="text-muted text-sm">{d.platform} · {d.rtmpUrl}</div>
                    <div className="text-xs text-muted mt-1">
                      {d.enabled ? 'Enabled' : 'Disabled'}
                      {d.lastConnectedAt ? ` · Last connected ${new Date(d.lastConnectedAt).toLocaleString()}` : ''}
                      {d.reconnectCount > 0 ? ` · Reconnects: ${d.reconnectCount}` : ''}
                    </div>
                  </div>
                </div>
                                <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
                   <Link to={`/streams/${streamId}/destinations/${d.id}/edit`}>
                     <Button variant="secondary" size="sm">Edit</Button>
                   </Link>
                  <CopyButton text={d.rtmpUrl} label="Copy URL" />
                  <Button variant="secondary" size="sm" onClick={() => testDest(d)} title="Test reachability">
                    Test
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => repush(d)} title="Retry the relay now">
                    ↻ Re-push
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => toggleEnabled(d)}>
                    {d.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => deleteDest(d)} title="Delete destination">
                    Delete
                  </Button>
                </div>
              </div>
              {d.lastError && <div className="mt-2 text-danger text-sm">{d.lastError}</div>}
              {d.bitrateKbps && <div className="mt-1 text-muted text-sm">{d.bitrateKbps} kbps · {d.fps} fps</div>}
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
